import React, { useEffect, useMemo } from 'react';
import { X } from 'lucide-react';
import {
  filterMatchesBySeries,
  filterMatchesByGroundName,
  filterMatchesByClubName,
  pickMatchColumns,
} from '../utils/matchesDrilldown';

/**
 * CricClubs match drill-down: series, ground, club (filter _matches); umpire (summary only).
 */
export default function MatchesDrillDownModal({ open, onClose, variant, seriesRow, groundRow, clubRow, umpireRow, matches }) {
  const filtered = useMemo(() => {
    if (!open || !variant) return [];
    if (variant === 'series' && seriesRow) {
      return filterMatchesBySeries(matches, seriesRow.series, seriesRow.sheet);
    }
    if (variant === 'ground' && groundRow?.name) {
      return filterMatchesByGroundName(matches, groundRow.name);
    }
    if (variant === 'club' && clubRow?.name) {
      return filterMatchesByClubName(matches, clubRow.name);
    }
    return [];
  }, [open, variant, seriesRow, groundRow, clubRow, matches]);

  const columns = useMemo(() => {
    if (!filtered.length) return [];
    return pickMatchColumns(filtered[0]);
  }, [filtered]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open || !variant) return null;

  const title =
    variant === 'series'
      ? `${seriesRow?.series || ''} (${seriesRow?.sheet || ''})`
      : variant === 'ground'
        ? groundRow?.name
        : variant === 'club'
          ? clubRow?.name
          : umpireRow?.name;

  return (
    <div
      className="drill-modal-backdrop"
      role="presentation"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="drill-modal-panel glass-card drill-modal-panel--wide" role="dialog" aria-modal="true">
        <div className="drill-modal-header">
          <div>
            <h2 className="drill-modal-title">{title || 'Details'}</h2>
            <p className="drill-modal-sub">
              {variant === 'umpire'
                ? 'Match officials (CricClubs aggregate) — per-match row list may not be linked in this table'
                : 'CricClubs match rows (kwata_final_matches)'}
            </p>
          </div>
          <button type="button" className="drill-modal-close" onClick={onClose} aria-label="Close">
            <X size={22} />
          </button>
        </div>

        {variant === 'umpire' && umpireRow && (
          <div className="drill-modal-stats" style={{ paddingBottom: '1rem' }}>
            <div className="drill-stat">
              <span className="drill-stat-val">{(umpireRow.match_count || 0).toLocaleString()}</span>
              <span className="drill-stat-lbl">Matches (officiated)</span>
            </div>
            <div className="drill-stat">
              <span className="drill-stat-val" style={{ fontSize: '0.9rem' }}>
                {umpireRow.level || '—'}
              </span>
              <span className="drill-stat-lbl">Level</span>
            </div>
          </div>
        )}

        {variant !== 'umpire' && (
          <>
            <div className="drill-modal-stats">
              <div className="drill-stat">
                <span className="drill-stat-val">{filtered.length}</span>
                <span className="drill-stat-lbl">Match rows</span>
              </div>
            </div>
            {filtered.length === 0 && (
              <p className="drill-modal-note" style={{ color: 'var(--text-muted)' }}>
                No rows matched (check field names on match records or ground/club spelling).
              </p>
            )}
            {filtered.length > 0 && (
              <div className="glass-card" style={{ padding: 0, overflow: 'auto', margin: '0 1.5rem 1.5rem' }}>
                <table className="data-table">
                  <thead>
                    <tr>
                      {columns.map((col) => (
                        <th key={col}>{col.replace(/_/g, ' ')}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {filtered.slice(0, 150).map((row, i) => (
                      <tr key={i}>
                        {columns.map((col) => (
                          <td key={col} style={{ fontSize: '0.78rem', maxWidth: 180 }}>
                            {row[col] != null && row[col] !== '' ? String(row[col]) : '—'}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
                {filtered.length > 150 && (
                  <p style={{ fontSize: '0.72rem', color: 'var(--text-muted)', padding: '0.5rem 1rem' }}>
                    Showing first 150 of {filtered.length} rows.
                  </p>
                )}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
